import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL, BASE_URL } from '../../config/apiConfig';
import { FaSave, FaSpinner, FaTrash, FaPlus, FaTimes, FaImage } from 'react-icons/fa';

const AdsManagement = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: null,
    imagePreview: ''
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAds(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Failed to load advertisements');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate type
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        toast.error('Only JPG and PNG images are allowed');
        return;
      }
      setFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.image) {
      toast.error('Title and Image are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('image', formData.image);

      const response = await axios.post(`${API_URL}/ads/upload`, data, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Advertisement uploaded successfully');
        resetForm();
        fetchAds();
      }
    } catch (error) {
      console.error('Error saving ad:', error);
      toast.error(error.response?.data?.message || 'Failed to save advertisement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Advertisement deleted successfully');
        fetchAds();
      }
    } catch (error) {
      console.error('Error deleting ad:', error);
      toast.error('Failed to delete advertisement');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', image: null, imagePreview: '' });
    setShowForm(false);
  };

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 pt-6 pb-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Advertisement Management</h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          {showForm ? <FaTimes /> : <FaPlus />} {showForm ? 'Close' : 'Add New Ad'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ad Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="E.g. Summer Sale 2026"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="Ad details..."
                    rows="4"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ad Image (2:3 Aspect Ratio) *</label>
                <div className="relative group">
                  <div className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-all ${formData.imagePreview ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}>
                    {formData.imagePreview ? (
                      <div className="relative">
                        <img src={formData.imagePreview} alt="Preview" className="max-h-64 rounded-lg shadow-md" />
                        <button 
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image: null, imagePreview: '' }))}
                          className="absolute -top-3 -right-3 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-lg"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <FaImage size={40} className="text-gray-400 mb-3" />
                        <p className="text-sm font-medium text-gray-600">Click to upload Ad Image</p>
                        <p className="text-xs text-gray-400 mt-1">Portrait (2:3 aspect ratio) only</p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      onChange={handleFileChange}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
                  <p className="text-xs text-orange-700">
                    <strong>Note:</strong> Uploaded images are strictly validated for a 2:3 ratio. If the ratio is incorrect, it will be automatically cropped.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-70 flex items-center gap-2 shadow-lg shadow-blue-200"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {saving ? 'Publishing...' : 'Publish Advertisement'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <FaSpinner className="animate-spin text-blue-600 text-4xl mb-4" />
          <p className="text-gray-500 animate-pulse">Loading advertisements...</p>
        </div>
      ) : ads.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
          <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaImage size={32} className="text-gray-300" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Advertisements Found</h3>
          <p className="text-gray-500 max-w-sm mx-auto">Get started by creating your first billboard or banner advertisement.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {ads.map((ad) => (
            <div key={ad._id} className="bg-white rounded-xl shadow-md overflow-hidden group border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="relative aspect-[2/3] overflow-hidden bg-gray-100">
                <img 
                  src={ad.imageUrl.startsWith('http') ? ad.imageUrl : `${BASE_URL}${ad.imageUrl}`} 
                  alt={ad.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                  <button 
                    onClick={() => handleDelete(ad._id)}
                    className="p-3 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-300"
                  >
                    <FaTrash size={18} />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-gray-900 truncate flex-1">{ad.title}</h3>
                  <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Active</span>
                </div>
                <p className="text-sm text-gray-500 line-clamp-2 h-10">{ad.description || 'No description provided.'}</p>
                <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between items-center text-[10px] text-gray-400 uppercase font-semibold">
                  <span>Created At</span>
                  <span>{new Date(ad.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdsManagement;
